/**
 * Route paths shared by US-Shop and US-Enrollment-BFF.
 *
 * Every path is under `/bff/` so a single ingress can path-route `/bff/*` → BFF
 * and `/` → Shop on ONE host (ADR-0002 §8), keeping the session cookie
 * first-party. US-Shop's Vite dev server proxies the same prefix, so dev and
 * prod are structurally identical rather than merely similar.
 */
export const BFF_ROUTES = {
    health: '/bff/health',
    session: '/bff/session',
    /** E-SF03 owns the handler. A `501` placeholder occupies it in E-SF01. */
    addressLookup: '/bff/address/lookup',
    /**
     * Address autocomplete (demo mock) — GET `?q=<partial>`. NO LONGER anonymous:
     * each suggestion now carries its catalog `esiid` (so the ZIP on-ramp calls
     * `esiidLookup` DIRECTLY on pick), which makes it a lookup that egresses esiids.
     * It is therefore SESSION-gated + captcha-gated + rate-limited (5/hour), like
     * `addressLookup` — see CAPTCHA_ROUTES + SENSITIVE_ROUTE_PATTERNS. See
     * address.ts + STUBS.md (incl. the captcha-on-GET caveat).
     */
    addressSuggest: '/bff/address/suggest',
    /**
     * The serviceable city+ZIP list — GET, no query. Powers the storefront ZIP
     * picker, so a shopper (and a tester) can browse or search the ZIPs we serve
     * instead of having to know one by heart.
     *
     * Session-gated but NOT captcha-gated, and the distinction is deliberate.
     * `addressSuggest` egresses catalog esiids, which is what earns it the
     * captcha. This route returns ZIP + city + a count — the service territory a
     * REP publishes anyway — and nothing that identifies a meter. Putting a
     * CAPTCHA on the control that populates a dropdown at page load would break
     * the picker for every honest shopper to protect data that is not secret.
     */
    /** Step one: name, email, phone — creates the lead before any meter is known. */
    contact: '/bff/contact',
    /**
     * The footer "Contact us" enquiry — name, email, free-text message → an
     * operator-visible enquiry row in Accounts. POST; session-scoped;
     * captcha-gated (an unauthenticated write that stores a row per call, the
     * same posture as `contact`). NOT a funnel step: it creates no prospect and
     * touches no funnel stores.
     */
    contactUs: '/bff/contact-us',
    serviceableZips: '/bff/address/zips',
    /**
     * The FUNNEL REHYDRATE SNAPSHOT — `GET /bff/funnel`. Session-resolved read-back
     * of the funnel state the BFF already holds, so a browser refresh no longer loses
     * the shopper's progress. See funnel.ts for the field-by-field inclusion rationale.
     *
     * ── ABUSE POSTURE: DELIBERATELY UNGATED, AND THAT IS THE POINT ────────────
     * It is NOT in `CAPTCHA_ROUTES` and it matches no `SENSITIVE_ROUTE_PATTERNS`
     * entry on the BFF. A CAPTCHA on a page-load rehydrate would break EVERY
     * refresh — which is the exact bug this route exists to fix. It mints nothing,
     * calls no upstream, and costs one funnel-session read; the baseline per-IP
     * (60/min) and per-session (30/min) rules already cover it.
     *
     * ⚠️ HONEST CAVEAT, recorded rather than glossed: this converts ONE
     * captcha-gated `/bff/esiid/lookup` into unlimited re-reads of that one result.
     * That is acceptable because the caller ALREADY HOLDS the data and can only ever
     * read their own signed session — there is no new oracle and nothing to walk.
     */
    funnel: '/bff/funnel',
    /**
     * SHOP-1 / SHOP-3 — resolve a raw ERCOT ESI ID to an enriched `MeterDetail`.
     * POST; session-scoped; captcha-gated. Currently a DUMMY-DATA STUB (deterministic
     * meter detail synthesized from the ESI ID digits) mirroring the demo-mock
     * posture of `/bff/address/suggest`. Both meter-first on-ramps (ESI ID typed
     * directly, or an ESI ID resolved from ZIP→address) converge on this route. See
     * meter.ts + STUBS.md.
     */
    esiidLookup: '/bff/esiid/lookup',
    /** E-SF05 owns the handler. A `501` placeholder occupies it in E-SF01. */
    creditCheck: '/bff/credit/check',
    /**
     * E-SF05 — issue an email OTP to the address the customer typed on the credit
     * step. POST. The response says WHEN the code expires, never WHAT it is, and
     * says nothing about whether the address is already known to any system.
     */
    emailVerificationSend: '/bff/email-verification/send',
    /**
     * E-SF05 — redeem the emailed OTP. POST. A `200` in every outcome
     * (verified/incorrect/expired/locked) — those are answers, not protocol errors.
     * `/bff/credit/check` refuses to run until this has succeeded for the address
     * on its `contact`, server-side.
     */
    emailVerificationVerify: '/bff/email-verification/verify',
    /** E-SF05 — create the Stripe (test-mode) deposit intent, or the badged stub. POST. */
    depositIntent: '/bff/deposit/intent',
    /** E-SF05 — confirm a collected deposit (server re-checks Stripe). POST. */
    depositConfirm: '/bff/deposit/confirm',
    /**
     * E-SF05 — put a MASKED card on file with NO Stripe (dev/demo capture). The
     * browser derives {brand,last4,expMonth,expYear} locally and sends only those;
     * the full PAN/CVV never reach the BFF. POST. Inherits the `deposit` abuse gate.
     */
    depositCardOnFile: '/bff/deposit/card-on-file',
    /** E-SF05 — record a §25.478 waiver election in lieu of the deposit (AC2). POST. */
    depositWaiverElection: '/bff/deposit/waiver-election',
    /** E-SF03 — out-of-area lead capture (AC2). POST; a lead in Accounts (fail-loud seam today). */
    lead: '/bff/lead',
    /**
     * E-SF20 — declare a build that has no meter yet. POST; session-required;
     * idempotency-keyed; captcha-gated. Reached from the `not_found` branch of
     * `addressLookup` when it reports `newServiceAvailable`.
     *
     * ⚠️ IT DECLARES, IT DOES NOT REQUEST. A REP cannot ask a TDU to install a
     * meter (see new-service.ts). This route records what the CUSTOMER told us and
     * starts us watching for the ESI ID; nothing is sent to anybody's TDU.
     */
    newServiceDeclare: '/bff/new-service/declare',
    /**
     * E-SF20 — the anonymous pending-service status read. GET; the ref in the path
     * IS the capability (no session, no login — the customer clicked a link in their
     * own email), exactly like `offerEntry` and `enrollmentStatus`.
     *
     * ⚠️ DELIBERATELY ABSENT FROM `CAPTCHA_ROUTES` and from the BFF's
     * `SENSITIVE_ROUTE_PATTERNS` (whose `new-service` entry is an exact match on
     * the declare POST). It is an anonymous read of the caller's OWN record,
     * reached from a link they were emailed; a Turnstile challenge there would put
     * a puzzle between a customer and the one page that tells them whether their
     * house has power yet. The ref is 256-bit and unguessable, so the risk is abuse
     * VOLUME rather than walking, which the baseline per-IP rule already bounds.
     *
     * It returns NO `esiid` — see new-service.ts. `assertResponseSchemasSafe` would
     * refuse to boot if it did, which is the guard working.
     */
    newServiceStatus: '/bff/new-service/:ref',
    /**
     * E-SF06 — the sign context (start-date constraints, domestic EFL/TOS/YRAC, the
     * deposit-settlement gate). GET; session-resolved. The at-accept saga owns the
     * handler — a pending state until it lands.
     */
    signContext: '/bff/sign/context',
    /**
     * E-SF06 — submit the click-wrap `SignatureEvent` and accept the self-serve
     * quote (`web_self_serve` instant-accept, ADR-0009). POST; idempotency-keyed.
     * The at-accept saga owns the handler — the FE fails loud until it lands.
     */
    signAccept: '/bff/sign/accept',
    /**
     * E-SF18 — the UNSIGNED contract PDF the customer reads BEFORE signing. GET
     * `?candidate=<id>&plan=<tariffId>`; session-resolved; proxies Quotation's E40
     * `GET /v1/us/quotes/:quoteId/documents/agreement-preview`.
     *
     * ⚠️ IT STREAMS `application/pdf`, NEVER A JSON BODY (E-SF18 §5.1), and the
     * reason is structural rather than stylistic. The document is resolved from the
     * funnel session's DRAFT quote, whose projection carries `esiid`; a JSON proxy
     * would have to declare a response schema for it, and
     * `assertResponseSchemasSafe` REFUSES TO BOOT on any schema declaring
     * `esiid`/`tenantId`/`ssn`/`dateOfBirth` outside its four-entry allow-list. A
     * byte stream has no JSON body to walk and no premise field to declare, so the
     * quoteId and the ESI ID physically cannot egress. `/bff/status/:token/quote.pdf`
     * is the precedent. If this is ever "improved" into a JSON endpoint carrying
     * document metadata, boot fails — that is the guard working, not a bug to
     * route around.
     *
     * ⚠️ DELIBERATELY ABSENT FROM `CAPTCHA_ROUTES` AND FROM the BFF's
     * `SENSITIVE_ROUTE_PATTERNS` (whose `sign` entry is an EXACT match on
     * `/bff/sign/accept`). It is a session-gated READ of the caller's OWN
     * in-progress enrollment: it resolves nothing the caller does not already hold,
     * mints nothing, and writes nothing. A Turnstile challenge in front of the
     * document a customer must read before signing would put a puzzle between them
     * and their own contract. The baseline per-IP (60/min) and per-session (30/min)
     * rules bound it, and the upstream render is cached per (quote, pricing
     * snapshot) by E40.
     */
    signAgreementPreview: '/bff/sign/agreement-preview.pdf',
    /**
     * E-SF04 — personalization ("your estimated bill"). GET; session-resolved
     * read (candidate + plan → GMO 365-day usage + Pricing calculate). Not a body
     * (read-only query); gated by a server-side free-allowance floor.
     */
    estimate: '/bff/estimate',
    /**
     * E-SF04 / QA-2026-08-05 BLOCKER 1 — persist the shopper's chosen home-size
     * profile on the funnel session. POST; idempotency-keyed; session-required.
     *
     * ── ABUSE POSTURE: BASELINE ONLY, AND THAT IS DELIBERATE ──────────────────
     * It matches no `SENSITIVE_ROUTE_PATTERNS` entry and is NOT in `CAPTCHA_ROUTES`.
     * It calls no upstream, mints nothing, costs one funnel-session write, and can
     * only ever write the CALLER'S OWN session with a value from a three-member
     * enum — there is nothing to enumerate and nothing to spend. Putting a
     * Turnstile challenge on "I live in a small home" would gate the storefront's
     * cheapest interaction while the per-IP (60/min) and per-session (30/min)
     * baseline rules already bound it.
     */
    usageProfile: '/bff/funnel/usage-profile',
    /**
     * E-SF04 — create the DRAFT `web_self_serve` quote so sign has a `quoteId`
     * (blocker 3). POST; idempotency-keyed; server-side quote create from the
     * funnel session. The resulting quoteId stays server-side (never egresses).
     */
    quoteDraft: '/bff/quote/draft',
    /**
     * E-SF16 — the tokenized offer entry (ADR-0010 D4). GET; the token in the path
     * IS the capability (no session, no login — the customer clicked a link in their
     * own email).
     *
     * ⚠️ IT ANSWERS 302, NEVER A JSON BODY (D4.1). The offer resolves to a premise,
     * and `assertResponseSchemasSafe` refuses to boot on any response schema
     * declaring `esiid`/`tenantId`/`ssn`/`dateOfBirth`. A redirect has no response
     * schema to walk, so the seeded premise physically cannot egress. If this is
     * ever "improved" into a JSON resolve endpoint carrying the premise, boot fails
     * — that is the guard working, not a bug to route around.
     *
     * ⚠️ IT IS DELIBERATELY ABSENT FROM `CAPTCHA_ROUTES` AND FROM THE BFF's
     * `SENSITIVE_ROUTE_PATTERNS` (D4.2). It matches none of the twelve patterns, so
     * boot does not demand a gate; adding it there would MECHANICALLY force a
     * Turnstile challenge in front of a link the customer clicked in their own
     * email, which is a funnel-killer. A 256-bit token is not enumerable — the risk
     * is abuse VOLUME, not guessing — so it carries a standalone per-IP rate-limit
     * rule instead (`OFFER_ENTRY_RULE`).
     */
    offerEntry: '/bff/offer/:token',
    /**
     * E-CRM04 — the "resume your enrollment" link a lead is emailed by Accounts
     * (`<storefront>/bff/resume/<token>`). GET; the token in the path IS the
     * capability. Same posture as `offerEntry`, for the same reasons: it answers a
     * 302 and never a JSON body (the redeemed state carries an ESI ID), it is NOT
     * captcha-gated (the customer clicked a link in their own inbox) and it carries
     * its own per-IP rule (`RESUME_ENTRY_RULE`). Unlike the offer link, Accounts
     * builds this URL pointing straight at the BFF path, so there is no SPA hand-off
     * page in front of it.
     */
    resumeEntry: '/bff/resume/:token',
    /**
     * E-CRM04 — the phone check for a resume link sent to an UNVERIFIED email.
     * POST `{ phone }`; session-scoped; captcha-gated (same floor as `contact`).
     * Match → the held lead is restored; mismatch → 422 with the tries left.
     * The segment `confirm` is a literal the log masker leaves alone.
     */
    resumeConfirm: '/bff/resume/confirm',
    /**
     * E-CRM04 — redeem the resume token the link's GET HELD (the GET itself never
     * redeems: mail scanners prefetch links and would spend the single-use token).
     * POST, no body; session-scoped; captcha-gated like `resumeConfirm`.
     */
    resumeContinue: '/bff/resume/continue',
    /** E-CRM04 — "Start over instead" on `/resume/continue`: drop the held token. */
    resumeContinueSkip: '/bff/resume/continue/skip',
    /** E-CRM04 — "Start over instead": discard the held lead. POST, session-scoped. */
    resumeConfirmSkip: '/bff/resume/confirm/skip',
    /** E-SF02 — plans shelf list. Server-side proxy to Pricing /v1/us/tariffs. */
    plans: '/bff/plans',
    /** E-SF02 — one plan's detail. Proxy to Pricing /v1/us/tariffs/:id. */
    planDetail: '/bff/plans/:id',
    /** E-SF02 — a plan's §25.475 price points. Proxy to Pricing /v1/us/tariffs/:id/price-points (cached). */
    planPricePoints: '/bff/plans/:id/price-points',
    /**
     * E-SF07 — the anonymous enrollment status page. GET; token-resolved (the
     * single-purpose, expiring, unguessable status token IS the capability — no
     * session, no login). Returns the lifecycle (pending/scheduled/active/rejected).
     */
    enrollmentStatus: '/bff/status/:token',
    /**
     * E-SF07 — the switch-hold self-serve resolution (AC1): New Occupant Statement
     * + proof-of-occupancy upload → a CRM ops case. POST; idempotency-keyed;
     * token-resolved. Fails loud (501) until the CRM ops-case endpoint lands.
     */
    newOccupantStatement: '/bff/status/:token/new-occupant',
};
/**
 * Paths where US-Shop must render a Turnstile widget and send a token.
 *
 * This is a CLIENT-RENDERING HINT ONLY. The authoritative gate is server-side
 * (`SENSITIVE_ROUTE_PATTERNS` in the BFF), and the thresholds behind it are
 * deliberately NOT published here: a client that knows it has "5 free
 * address lookups" is a client that has been told exactly how much abuse is
 * free. The server decides; the client is merely told to draw the widget.
 */
export const CAPTCHA_ROUTES = [
    BFF_ROUTES.addressLookup,
    // The footer enquiry is an anonymous row-per-call write into the operator
    // queue — widget renders here, BFF gates it server-side (5/hour floor).
    BFF_ROUTES.contactUs,
    // SHOP-1 — the address SUGGEST now egresses esiids, so it is session- +
    // captcha- + rate-limited like the lookup family. ⚠️ CAVEAT: it is an
    // autocomplete GET; per-request single-use Turnstile is impractical in prod, so
    // the effective controls are session + rate-limit (in dev CAPTCHA_PROVIDER=null
    // makes the token a no-op). The FE sends `x-captcha-token` on this GET. See STUBS.md.
    BFF_ROUTES.addressSuggest,
    // SHOP-1 / SHOP-3 — the ESI ID lookup is a sensitive public POST (resolves a
    // meter from a customer-typed ESI ID); the widget renders here and the BFF
    // gates it server-side. Same posture as the address lookup.
    BFF_ROUTES.esiidLookup,
    BFF_ROUTES.creditCheck,
    // E-SF05 — the deposit money-write also renders a widget (server-gated with a
    // modest free allowance; the widget drops in without a contract change).
    BFF_ROUTES.depositIntent,
    // NOTE: `depositCardOnFile` and `depositConfirm` are deliberately NOT listed —
    // they are follow-up deposit writes in the SAME step where the widget already
    // rendered at `depositIntent`, and rendering a second widget there is worse UX.
    // Both still inherit the server-side `deposit` abuse gate (wildcard, N=10/hr).
    // E-SF06 — the contract write (sign & accept) is a sensitive, expensive public
    // POST; the widget renders here and the BFF gates it server-side.
    BFF_ROUTES.signAccept,
    // E-SF07 — the switch-hold resolution POST creates a CRM ops case (an
    // anonymous, side-effecting write that forwards an uploaded document); the
    // widget renders here and the BFF gates it server-side.
    BFF_ROUTES.newOccupantStatement,
    // E-SF20 — the new-service DECLARE. An anonymous write that stores a street
    // address, an email and build facts, and enrols that record into a polling loop
    // that runs for weeks. The widget renders here and the BFF gates it server-side.
    // ⚠️ Only the DECLARE. `newServiceStatus` is deliberately absent — it is the
    // emailed link a customer follows to find out whether their house has power.
    BFF_ROUTES.newServiceDeclare,
    // E-SF05 — the OTP SEND. An anonymous POST whose side effect lands in a THIRD
    // PARTY'S INBOX, so the widget renders here and the BFF gates it server-side.
    // ⚠️ The VERIFY route is deliberately NOT listed: it is also server-gated, but
    // it renders no widget (a customer typing a 6-digit code should not also be
    // asked to prove they are human), and adding it here would put a Turnstile
    // challenge in the middle of the OTP entry step.
    BFF_ROUTES.emailVerificationSend,
];
//# sourceMappingURL=routes.js.map